import { Equipment, Location, Lock, Station, StationConnectivity, StationHealth, SyncLogEntry } from './models/assets';
import { RentalDuration } from './models/rental';
import Service from './Service';

class AssetsService {
  private static URL_BASE = process.env.REACT_APP_URL_API;

  private static URL_LOCATIONS = () => `${this.URL_BASE}/assets/admin/locations`;

  private static URL_STATIONS = (locationId: number) => `${this.URL_BASE}/assets/admin/location/${locationId}/stations`;

  private static URL_RENTAL_DURATIONS = (locationId: number) => `${this.URL_BASE}/assets/admin/location/${locationId}/rental-durations`;

  private static URL_LOCATION_CREATE = () => `${this.URL_BASE}/assets/admin/location/create`;

  private static URL_LOCATION = (value: number | string) => `${this.URL_BASE}/assets/admin/location/${value}`;

  private static URL_EQUIPMENTS = () => `${this.URL_BASE}/assets/admin/equipments`;

  private static URL_STATION_EQUIPMENTS = (stationId: number) => `${this.URL_BASE}/assets/admin/station/${stationId}/equipments`;

  private static URL_EQUIPMENT = (id?: number) => `${this.URL_BASE}/assets/admin/equipment?id=${id}`;

  private static URL_EQUIPMENT_CREATE = () => `${this.URL_BASE}/assets/admin/equipment/create`;

  private static URL_STATION_CREATE = () => `${this.URL_BASE}/assets/admin/station/link`;

  private static URL_UPLOADIMAGE = () => `${this.URL_BASE}/upload-image`;

  private static URL_STATION = (action: 'activate' | 'deactivate') => `${this.URL_BASE}/assets/admin/station/${action}`;

  static getLocations(): Promise<{ locations: Location[] }> {
    return Service.get(this.URL_LOCATIONS());
  }

  static getRentalDurations(locationId: number): Promise<{ rentalDurations: RentalDuration[] }> {
    return Service.get(this.URL_RENTAL_DURATIONS(locationId));
  }

  static getStations(locationId: number): Promise<{ stations: Station[] }> {
    return Service.get(this.URL_STATIONS(locationId));
  }

  static getLocation(id: number): Promise<{ location: Location }> {
    return Service.get(this.URL_LOCATION(id));
  }

  static updateLocation(id: number, location: Partial<Location>): Promise<{ location: Location }> {
    return Service.patch(this.URL_LOCATION(id), location);
  }

  static createLocation(location: Partial<Location>): Promise<{ location: Location }> {
    return Service.post(this.URL_LOCATION_CREATE(), {
      name: location.name,
      description: location.description,
      addressStreet: location.addressStreet,
      addressStreet2: location.addressStreet2,
      addressCity: location.addressCity,
      addressPostalCode: location.addressPostalCode,
      addressCountry: 'France', // todo: add country
      websiteUrl: location.websiteUrl,
      termsUrl: location.termsUrl,
      schedules: location.schedules,
    });
  }

  static linkStation(station: Partial<Station>): Promise<{ station: Station }> {
    return Service.post(this.URL_STATION_CREATE(), {
      token: station.token,
      lat: station.lat,
      lng: station.lng,
      locationId: station.locationId,
    });
  }

  static openLock(stationToken: string, lockId: number): Promise<any> {
    return Service.post(`${this.URL_BASE}/v1/stations/${stationToken}/locks/${lockId}/unlock`, {});
  }

  static uploadImage(image: File): Promise<{ url: string }> {
    const formData = new FormData();

    formData.append('image', image);
    return Service.post(this.URL_UPLOADIMAGE(), formData);
  }

  static getEquipments(): Promise<{ equipments: Equipment[] }> {
    return Service.get(this.URL_EQUIPMENTS());
  }

  static getStationEquipments(stationId: number): Promise<{ equipments: Equipment[] }> {
    return Service.get(this.URL_STATION_EQUIPMENTS(stationId));
  }

  static updateEquipment(
    equipmentId: number,
    equipment: Partial<Equipment>,
    equipmentPrices: { id?: number; price: number }[],
  ): Promise<{ success: boolean }> {
    return Service.patch(this.URL_EQUIPMENT(equipmentId), {
      equipment,
      equipmentPrices,
    });
  }

  static createEquipment(equipment: Partial<Equipment>, equipmentPrices: { price: number; rentalDuration: { id: number } }[]): Promise<{ success: boolean }> {
    return Service.post(this.URL_EQUIPMENT_CREATE(), {
      equipment,
      equipmentPrices,
    });
  }

  static updateStation(stationId: number, data: { name?: string }): Promise<{ station: Station }> {
    return Service.patch(`${this.URL_BASE}/assets/admin/station`, {
      stationId,
      ...data,
    });
  }

  static activateStation(stationId: number): Promise<{ station: Station }> {
    return Service.post(this.URL_STATION('activate'), {
      stationId,
    });
  }

  static deactivateStation(stationId: number, message?: string): Promise<{ station: Station }> {
    return Service.post(this.URL_STATION('deactivate'), {
      stationId,
      message,
    });
  }

  static activateLocation(locationId: number): Promise<{ station: Station }> {
    return Service.post(this.URL_LOCATION('activate'), {
      locationId,
    });
  }

  static deactivateLocation(locationId: number): Promise<{ station: Station }> {
    return Service.post(this.URL_LOCATION('deactivate'), {
      locationId,
    });
  }

  static toggleLockActive(lockId: number, action: 'activate' | 'deactivate'): Promise<{ lock: Lock }> {
    return Service.post(`${this.URL_BASE}/assets/admin/lock`, { lockId, action });
  }

  // Assign an equipment to a lock (moving it off any previous lock), or — when
  // equipmentId is omitted — clear the lock, removing the equipment from it.
  static updateLockEquipment(lockId: number, equipmentId?: number): Promise<{ success: boolean }> {
    return Service.patch(`${this.URL_BASE}/assets/admin/lock`, {
      lockId,
      ...(equipmentId != null ? { equipmentId } : {}),
    });
  }

  static getStationSyncHistory(
    stationToken: string,
    from?: Date,
    to?: Date,
  ): Promise<{ logs: SyncLogEntry[] }> {
    const params = new URLSearchParams();
    if (from) params.set('from', from.toISOString());
    if (to) params.set('to', to.toISOString());
    const qs = params.toString();
    const url = `${this.URL_BASE}/v1/stations/${stationToken}/sync-history${qs ? `?${qs}` : ''}`;
    return Service.get(url);
  }

  static rebootStation(stationToken: string): Promise<{ id: number; a: string }> {
    return Service.post(`${this.URL_BASE}/v1/stations/${stationToken}/reboot`, {});
  }

  // Reconstructed disconnection windows for a station over a period (free admin endpoint).
  static getStationConnectivity(
    stationToken: string,
    from?: Date,
    to?: Date,
  ): Promise<StationConnectivity> {
    const params = new URLSearchParams();
    if (from) params.set('from', from.toISOString());
    if (to) params.set('to', to.toISOString());
    const qs = params.toString();
    return Service.get(`${this.URL_BASE}/admin/stations/${stationToken}/connectivity${qs ? `?${qs}` : ''}`);
  }

  // Cross-station health digest, optionally scoped to a location (free admin endpoint).
  static getStationsHealth(locationId?: number): Promise<{ stations: StationHealth[] }> {
    const qs = locationId != null ? `?location_id=${locationId}` : '';
    return Service.get(`${this.URL_BASE}/admin/stations/health${qs}`);
  }
}

export default AssetsService;
